import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Plus, Trash2, GripVertical, Save, ArrowLeft, X, Play, Zap, Upload, FileText, Video, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InstructorLessons() {
  const { courseId } = useParams();
  const { lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [course, setCourse] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editLesson, setEditLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const pdfInputRef = useRef(null);
  const [form, setForm] = useState({
    title: '', title_tr: '', description: '', description_tr: '',
    order: 1, lesson_type: 'video', vimeo_url: '', content: '', content_tr: '',
    duration_minutes: 0, pdf_file_id: '', pdf_filename: '', materials: []
  });

  const fetchData = async () => {
    try {
      const [lessonsRes, courseRes] = await Promise.all([
        axios.get(`${API}/instructor/courses/${courseId}/lessons`, { headers: authHeaders() }),
        axios.get(`${API}/courses/${courseId}`)
      ]);
      setLessons(lessonsRes.data.lessons);
      setCourse(courseRes.data.course);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [courseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => ({
    title: '', title_tr: '', description: '', description_tr: '',
    order: lessons.length + 1, lesson_type: 'video', vimeo_url: '', content: '', content_tr: '',
    duration_minutes: 0, pdf_file_id: '', pdf_filename: '', materials: []
  });

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPdf(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload/pdf`, formData, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      setForm(prev => ({
        ...prev,
        pdf_file_id: res.data.file_id,
        pdf_filename: res.data.filename,
        materials: [...(prev.materials || []), { file_id: res.data.file_id, filename: res.data.filename, type: 'pdf' }]
      }));
    } catch (err) {
      console.error('PDF upload error:', err);
      toast.error(lang === 'tr' ? 'PDF yüklenemedi. Tekrar deneyin.' : 'PDF upload failed. Try again.');
    } finally {
      setUploadingPdf(false);
    }
  };

  const removePdf = () => {
    setForm(prev => ({
      ...prev,
      pdf_file_id: '',
      pdf_filename: '',
      materials: (prev.materials || []).filter(m => m.type !== 'pdf')
    }));
  };

  const handleSave = async () => {
    if (!form.title) return;
    try {
      if (editLesson) {
        await axios.put(`${API}/instructor/lessons/${editLesson.lesson_id}`, form, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/instructor/lessons`, { ...form, course_id: courseId }, { headers: authHeaders() });
      }
      setShowForm(false);
      setEditLesson(null);
      setForm(resetForm());
      fetchData();
    } catch (err) { console.error(err); }
  };

  const deleteLesson = async (lessonId) => {
    if (!window.confirm(lang === 'tr' ? 'Dersi silmek istediğinize emin misiniz?' : 'Delete this lesson?')) return;
    try {
      await axios.delete(`${API}/instructor/lessons/${lessonId}`, { headers: authHeaders() });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const startEdit = (lesson) => {
    setEditLesson(lesson);
    setForm({
      title: lesson.title || '', title_tr: lesson.title_tr || '', description: lesson.description || '',
      description_tr: lesson.description_tr || '', order: lesson.order || 1, lesson_type: lesson.lesson_type || 'video',
      vimeo_url: lesson.vimeo_url || '', content: lesson.content || '', content_tr: lesson.content_tr || '',
      duration_minutes: lesson.duration_minutes || 0, pdf_file_id: lesson.pdf_file_id || '',
      pdf_filename: lesson.pdf_filename || '', materials: lesson.materials || []
    });
    setShowForm(true);
  };

  const startNew = () => {
    setEditLesson(null);
    setForm(resetForm());
    setShowForm(true);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-app)]">
      <div className="w-10 h-10 border-4 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[var(--bg-app)] py-8" data-testid="instructor-lessons-page">
      <div className="max-w-4xl mx-auto px-4">
        <button onClick={() => navigate('/instructor')} className="flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--text-main)] mb-6 font-bold text-sm">
          <ArrowLeft size={18} /> {lang === 'tr' ? 'Geri' : 'Back'}
        </button>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-2xl font-bold">{lang === 'tr' ? 'Ders Yonetimi' : 'Lesson Management'}</h1>
            <p className="text-sm text-[var(--text-muted)] font-semibold">{course?.title} - {lessons.length} {lang === 'tr' ? 'ders' : 'lessons'}</p>
          </div>
          <button onClick={startNew} className="btn-primary px-4 py-2 flex items-center gap-2 text-sm" data-testid="add-lesson-btn">
            <Plus size={16} /> {lang === 'tr' ? 'Ders Ekle' : 'Add Lesson'}
          </button>
        </div>

        {/* Lesson Form */}
        {showForm && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="card-clay p-6 mb-6 space-y-4" data-testid="lesson-form">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg">
                {editLesson ? (lang === 'tr' ? 'Ders Düzenle' : 'Edit Lesson') : (lang === 'tr' ? 'Yeni Ders' : 'New Lesson')}
              </h3>
              <button onClick={() => { setShowForm(false); setEditLesson(null); }} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
            </div>

            {/* Title Fields */}
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">Title (EN)</label>
                <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Introduction to Variables"
                  className="w-full px-3 py-2.5 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 focus:border-[var(--brand-primary)] focus:outline-none font-semibold text-sm"
                  data-testid="lesson-title-input" />
              </div>
              <div>
                <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">Baslik (TR)</label>
                <input type="text" value={form.title_tr} onChange={e => setForm({ ...form, title_tr: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 focus:border-[var(--brand-primary)] focus:outline-none font-semibold text-sm"
                  data-testid="lesson-title-tr-input" />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">{lang === 'tr' ? 'Açıklama' : 'Description'}</label>
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2}
                className="w-full px-3 py-2.5 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 focus:border-[var(--brand-primary)] focus:outline-none font-semibold text-sm"
                data-testid="lesson-desc-input" />
            </div>

            {/* Type, Order, Duration */}
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">{lang === 'tr' ? 'Ders Tipi' : 'Lesson Type'}</label>
                <select value={form.lesson_type} onChange={e => setForm({ ...form, lesson_type: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 focus:outline-none font-semibold text-sm"
                  data-testid="lesson-type-select">
                  <option value="video">Video</option>
                  <option value="interactive">{lang === 'tr' ? 'Interaktif (Quiz)' : 'Interactive (Quiz)'}</option>
                  <option value="pdf">PDF {lang === 'tr' ? 'Dokuman' : 'Document'}</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">{lang === 'tr' ? 'Sira' : 'Order'}</label>
                <input type="number" value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 1 })}
                  className="w-full px-3 py-2.5 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 focus:outline-none font-semibold text-sm" data-testid="lesson-order-input" />
              </div>
              <div>
                <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">{lang === 'tr' ? 'Süre (dakika)' : 'Duration (min)'}</label>
                <input type="number" value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2.5 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 focus:outline-none font-semibold text-sm" data-testid="lesson-duration-input" />
              </div>
            </div>

            {/* Vimeo URL */}
            {(form.lesson_type === 'video' || form.lesson_type === 'interactive') && (
              <div>
                <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block flex items-center gap-1">
                  <Video size={14} /> Vimeo URL
                </label>
                <input type="text" value={form.vimeo_url} onChange={e => setForm({ ...form, vimeo_url: e.target.value })}
                  placeholder="https://player.vimeo.com/video/123456789"
                  className="w-full px-3 py-2.5 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 focus:outline-none font-semibold text-sm" data-testid="lesson-vimeo-input" />
                <p className="text-xs text-[var(--text-muted)] mt-1">
                  {lang === 'tr' ? 'Vimeo videosunun embed URL\'sini yapistirin. Ornek: https://player.vimeo.com/video/76979871' : 'Paste the Vimeo embed URL. Example: https://player.vimeo.com/video/76979871'}
                </p>
              </div>
            )}

            {/* PDF Upload */}
            <div className="border-2 border-dashed border-[var(--border-light)] rounded-xl p-5">
              <label className="text-xs font-bold text-[var(--text-muted)] mb-3 block flex items-center gap-1">
                <FileText size={14} /> PDF {lang === 'tr' ? 'Ders Materyali' : 'Lesson Material'}
              </label>
              
              {form.pdf_filename ? (
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl border-2 border-[var(--brand-success)]">
                  <FileText size={24} className="text-[var(--brand-success)]" />
                  <div className="flex-1">
                    <p className="font-bold text-sm">{form.pdf_filename}</p>
                    <p className="text-xs text-[var(--brand-success)] font-semibold flex items-center gap-1">
                      <CheckCircle size={12} /> {lang === 'tr' ? 'Yüklendi' : 'Uploaded'}
                    </p>
                  </div>
                  <button onClick={removePdf} className="p-2 rounded-lg bg-red-50 text-red-500 hover:bg-red-100" data-testid="remove-pdf-btn">
                    <Trash2 size={16} />
                  </button>
                </div>
              ) : (
                <div className="text-center py-4">
                  <input type="file" ref={pdfInputRef} accept=".pdf" onChange={handlePdfUpload} className="hidden" />
                  <button onClick={() => pdfInputRef.current?.click()} disabled={uploadingPdf}
                    className="btn-tactile px-6 py-3 bg-white border-[var(--border-light)] inline-flex items-center gap-2" data-testid="upload-pdf-btn">
                    <Upload size={18} />
                    {uploadingPdf 
                      ? (lang === 'tr' ? 'PDF Yükleniyor...' : 'Uploading PDF...')
                      : (lang === 'tr' ? 'PDF Yükle' : 'Upload PDF')}
                  </button>
                  <p className="text-xs text-[var(--text-muted)] mt-2">{lang === 'tr' ? 'Maks 50MB PDF dosyasi' : 'Max 50MB PDF file'}</p>
                </div>
              )}
            </div>

            {/* Text Content */}
            <div>
              <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">{lang === 'tr' ? 'Metin İçeriği (Opsiyonel)' : 'Text Content (Optional)'}</label>
              <textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={3}
                placeholder={lang === 'tr' ? 'Ders notları, açıklamalar...' : 'Lesson notes, explanations...'}
                className="w-full px-3 py-2.5 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 focus:outline-none font-semibold text-sm" />
            </div>

            {/* Save */}
            <div className="flex gap-3">
              <button onClick={handleSave} disabled={!form.title} className="btn-success px-8 py-3 text-sm flex items-center gap-2 flex-1 justify-center disabled:opacity-50" data-testid="save-lesson-btn">
                <Save size={16} /> {lang === 'tr' ? 'Dersi Kaydet' : 'Save Lesson'}
              </button>
              <button onClick={() => { setShowForm(false); setEditLesson(null); }} className="btn-tactile px-6 py-3 text-sm bg-white border-[var(--border-light)]">
                {lang === 'tr' ? 'İptal' : 'Cancel'}
              </button>
            </div>
          </motion.div>
        )}

        {/* Lesson List */}
        <div className="space-y-3">
          {lessons.map((lesson, i) => (
            <motion.div key={lesson.lesson_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="card-clay p-4 flex items-center gap-4" data-testid={`instructor-lesson-${i}`}>
              <div className="text-[var(--text-muted)]"><GripVertical size={18} /></div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                lesson.lesson_type === 'interactive' ? 'bg-[var(--brand-secondary)]' : 
                lesson.lesson_type === 'pdf' ? 'bg-red-50' : 'bg-gray-100'
              }`}>
                {lesson.lesson_type === 'interactive' ? <Zap size={18} className="text-[var(--text-main)]" /> :
                 lesson.lesson_type === 'pdf' ? <FileText size={18} className="text-red-500" /> :
                 <Play size={18} className="text-[var(--text-muted)]" />}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-sm truncate">{lesson.title}</h4>
                <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                  <span className="capitalize">{lesson.lesson_type}</span>
                  {lesson.duration_minutes > 0 && <span>- {lesson.duration_minutes} min</span>}
                  {lesson.pdf_filename && (
                    <span className="flex items-center gap-1 text-red-500">
                      <FileText size={10} /> {lesson.pdf_filename}
                    </span>
                  )}
                  {lesson.vimeo_url && (
                    <span className="flex items-center gap-1 text-[var(--brand-primary)]">
                      <Video size={10} /> Vimeo
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => startEdit(lesson)} className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100" data-testid={`edit-lesson-${i}`}>
                  <Save size={14} />
                </button>
                <button onClick={() => deleteLesson(lesson.lesson_id)} className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100" data-testid={`delete-lesson-${i}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </motion.div>
          ))}
          {lessons.length === 0 && !showForm && (
            <div className="card-clay p-12 text-center">
              <Plus size={48} className="mx-auto text-[var(--text-muted)] mb-4" />
              <p className="font-display text-lg font-bold text-[var(--text-muted)] mb-4">
                {lang === 'tr' ? 'Henuz ders yok' : 'No lessons yet'}
              </p>
              <button onClick={startNew} className="btn-primary px-8 py-3" data-testid="create-first-lesson-btn">
                {lang === 'tr' ? 'Ilk Dersi Ekle' : 'Add First Lesson'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
